type El = { [field: string]: string | null };
export type OverrideableListElement<T extends El, Flag> = {
  original: T | null;
  deleted: boolean;
  inserted: T | null;
  flags: { [field: string]: Flag };
  overrides: { [field: string]: string };
};

export const deleted = <T extends El, Flag>(
  el: OverrideableListElement<T, Flag>
): OverrideableListElement<T, Flag> => {
  return { ...el, deleted: true };
};

export const inserted = <T extends El, Flag>(
  val: T
): OverrideableListElement<T, Flag> => {
  return {
    original: null,
    deleted: false,
    inserted: val,
    overrides: {},
    flags: {},
  };
};

export const overriden = <T extends El, Flag>(
  el: OverrideableListElement<T, Flag>,
  overrides: { [field in keyof El]: string }
): OverrideableListElement<T, Flag> => {
  if (el.inserted) {
    return { ...el, inserted: { ...el.inserted, ...overrides } };
  }

  const newOverrides: { [field in keyof El]: string } = { ...el.overrides };

  for (const [field, value] of Object.entries(overrides)) {
    if (el.original?.[field] !== value) {
      newOverrides[field] = value;
    } else {
      delete newOverrides[field];
    }
  }

  return { ...el, overrides: newOverrides };
};

export const effectiveValues = <T extends El, Flag>(
  el: OverrideableListElement<T, Flag>
): T | undefined => {
  if (el.deleted) {
    return undefined;
  }
  if (el.inserted) {
    return el.inserted;
  }

  if (el.original) {
    return { ...el.original, ...el.overrides };
  }
};
